/**
 * `<GmSlots>` — the content of the GM's **SETTINGS** rail page: the browser replacement
 * for `lvtt game define-slot`/`set-slot-role`/`remove-slot`/`set-password`. The page says
 * "users" and "seats"; the domain, the wire and the CLI say "slot" (GLOSSARY.md). One seat
 * row is one slot.
 *
 * Reads the app-wide Game session directly (mirrors {@link GmDirectivePanel}, the closest
 * analog) — self-gates GM-only, renders nothing for a non-GM (the server's `isGm` gate on
 * `game_command` is the real wall; this is just the affordance). The roster's pilots come
 * in as a prop, only to name the pilot each seat flies (pilots owned by the seat's slot id).
 *
 * Layout: a summary strip (seats, linked now, room lock), 01 GAME MASTER (the viewer's own
 * seat, then any co-GM seats), 02 PLAYERS (one row per player seat, tapping a row expands
 * its controls), ADD A SEAT, and 03 ROOM LOCK.
 *
 * **The list is rendered off `session.slots` ONLY — there is no local slot list state
 * here.** `session.slots` is the broadcast-driven `SlotPresence[]` (`useGameSession.ts`'s
 * `slotsFromState`, rebuilt from every `game_change`), so a slot defined by this GM, by a
 * concurrent `lvtt game define-slot`, or by another GM tab, all show up here identically.
 * Removal follows the same discipline: a removed row disappears only once the broadcast
 * drops it from `session.slots`, never optimistically on the click.
 *
 * Slot-id minting: the typed NAME is what the operator controls; the wire `slotId` is
 * caller-minted via `mintSlotId` (`../../lib/game/slotAdmin.ts`) — see that module's header.
 *
 * **Every guard here leads rather than reacts**, because a rejected `game_command` is a silent
 * no-op on the wire (no `game_reject`, no broadcast — `GameRoom.applyWireCommand`'s reject
 * branch sends only the routine `game_undo_state`). So the page never offers a doomed action:
 *  - **The viewer's own seat has no controls.** Demoting or removing your own seat would cut
 *    the GM out from under the page they are using; the page refuses it outright, a narrower
 *    rule than the reducer's.
 *  - **The room's only `gm` seat has no controls** — `isOnlyGmSlot` (`slotAdmin.ts`) mirrors
 *    the exact `gmCount <= 1` condition the reducer hard-rejects both `remove_slot` and a
 *    demoting `set_slot_role` on (`packages/domain/src/game/reducer.ts`).
 *  - **REMOVE… always asks first**, inline in the expanded row, and the question warns when
 *    the seat is linked: removing a linked seat drops that player's connection
 *    (`evictOrphanedConnections`, `slot-removal-kick.e2e.ts`). Their pilots stay in the roster.
 *
 * **Room password — a DIFFERENT wire path, on purpose.** `session.setRoomPassword` sends
 * `game_set_password` directly (never a `game_command`), because the server hashes the
 * plaintext (argon2id) before it becomes `set_room_password` — see `useGameSession.ts`. The
 * lock state renders off **`session.passwordSet`** (broadcast `PublicGameState.passwordSet`),
 * never local state; the wire never carries the hash. The typed password is transient input
 * only: sent once, cleared at once, never rendered back.
 */
import { useId, useState } from "react";
import type { PilotData } from "@lancer-vtt/domain";
import { NotSentNote, useNotSentNote } from "../../lib/linkHealth";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import type { SlotPresence } from "../../lib/game/useGameSession";
import { canAdminSlots, isOnlyGmSlot } from "../../lib/game/slotAdmin";
import { pilotCallsignLabel } from "./pilotLabel";
import "./brutalist-editor.css";

/**
 * The line under a seat's name: the pilot it flies, as `CALLSIGN · Name`, from the roster's
 * pilots owned by that slot id; `+N` when it owns more than one; `NO PILOT YET` when none.
 */
export function seatPilotLine(pilots: Record<string, PilotData>, slotId: string): string {
  const owned = Object.values(pilots)
    .filter((p) => p.owner === slotId)
    .sort((a, b) => pilotCallsignLabel(a.callsign).localeCompare(pilotCallsignLabel(b.callsign)));
  const first = owned[0];
  if (first === undefined) return "NO PILOT YET";
  const name = first.name.trim();
  const line = name === "" ? pilotCallsignLabel(first.callsign) : `${pilotCallsignLabel(first.callsign)} · ${name}`;
  return owned.length > 1 ? `${line} +${owned.length - 1}` : line;
}

function SeatStatus({ linked }: { linked: boolean }) {
  return (
    <span className={`gms-seat-status${linked ? " linked" : ""}`}>
      <span className="gms-seat-status-mark" aria-hidden />
      {linked ? "LINKED" : "OPEN"}
    </span>
  );
}

export function GmSlots({ pilots }: { pilots: Record<string, PilotData> }) {
  const session = useGameSessionContext();
  const ids = useId();
  const [name, setName] = useState("");
  // The one expanded seat row, and the seat whose REMOVE… is awaiting its confirm.
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // The typed room password — purely transient local input state, sent once
  // and cleared immediately; never rendered back to the screen, never persisted here.
  const [pwInput, setPwInput] = useState("");
  const { notSent, report } = useNotSentNote();

  // GM-gate: this whole surface is GM-only — mirrors GmDirectivePanel's `canDirect` gate.
  // The emit method (`defineSlot`) also no-ops for a non-GM; the server gate is the wall.
  if (!canAdminSlots(session.role)) return null;

  const trimmed = name.trim();
  const create = () => {
    if (trimmed === "") return;
    // `defineSlot` returns `null` when the send was a local no-op (socket not open / not
    // authenticated). Only clear the typed name when it actually went out — the preserved
    // input IS the feedback.
    const sent = session.defineSlot(trimmed);
    if (report(sent !== null)) setName("");
  };

  const slots = [...session.slots].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  const you = slots.find((s) => s.id === session.actorId);
  const coGms = slots.filter((s) => s.role === "gm" && s !== you);
  const players = slots.filter((s) => s.role === "player");
  const linkedCount = slots.filter((s) => s.connected).length;

  const toggle = (slotId: string) => {
    setOpenId(openId === slotId ? null : slotId);
    setConfirmId(null);
  };
  const setRole = (slotId: string, role: "gm" | "player") => {
    // The row moves section once the broadcast lands; close it so it doesn't reopen there.
    if (report(session.setSlotRole(slotId, role))) setOpenId(null);
  };
  const remove = (slotId: string) => {
    if (report(session.removeSlot(slotId))) {
      setConfirmId(null);
      setOpenId(null);
    }
  };

  const setPassword = () => {
    if (pwInput === "") return;
    // Kept on a refusal (link down): the GM retries without retyping.
    if (report(session.setRoomPassword(pwInput))) setPwInput(""); // never leave the plaintext sitting in the field once sent
  };
  const clearPassword = () => {
    if (report(session.setRoomPassword(null))) setPwInput("");
  };

  const seatRow = (slot: SlotPresence) => {
    const expanded = openId === slot.id;
    const confirming = confirmId === slot.id;
    const locked = isOnlyGmSlot(slots, slot.id);
    const controlsId = `${ids}-seat-${slot.id}`;
    return (
      <div key={slot.id} className={`gms-seat${expanded ? " expanded" : ""}`}>
        <button
          type="button"
          className="gms-seat-toggle"
          aria-expanded={expanded}
          aria-controls={controlsId}
          aria-label={`${slot.name}, ${slot.connected ? "linked" : "open"}, seat controls`}
          onClick={() => toggle(slot.id)}
        >
          <span className="gms-seat-who">
            <span className="gms-seat-name">{slot.name}</span>
            <span className="gms-seat-flies">{seatPilotLine(pilots, slot.id)}</span>
          </span>
          <SeatStatus linked={slot.connected} />
          <span className="gms-seat-chevron" aria-hidden />
        </button>
        {expanded && (
          <div className="gms-seat-controls" id={controlsId}>
            {locked ? (
              <span className="gms-seat-note">The room's only GM seat can't be demoted or removed.</span>
            ) : confirming ? (
              <>
                <span className="gms-seat-confirm" role="alert">
                  <b>Remove {slot.name}'s seat?</b>{" "}
                  {slot.connected ? "They are linked right now and will be dropped. " : ""}Their pilot stays in the roster.
                </span>
                <span className="gms-seat-actions">
                  <button type="button" className="be-btn be-btn-primary gms-seat-remove" aria-label={`Confirm remove ${slot.name}`} onClick={() => remove(slot.id)}>
                    REMOVE SEAT
                  </button>
                  <button type="button" className="be-btn" aria-label={`Cancel remove ${slot.name}`} onClick={() => setConfirmId(null)}>
                    KEEP
                  </button>
                </span>
              </>
            ) : (
              <span className="gms-seat-actions">
                {slot.role === "player" ? (
                  <button type="button" className="be-btn" aria-label={`Promote ${slot.name} to GM`} onClick={() => setRole(slot.id, "gm")}>
                    MAKE CO-GM
                  </button>
                ) : (
                  <button type="button" className="be-btn" aria-label={`Demote ${slot.name} to player`} onClick={() => setRole(slot.id, "player")}>
                    MAKE PLAYER
                  </button>
                )}
                <button type="button" className="be-btn be-btn-danger" aria-label={`Remove ${slot.name}`} onClick={() => setConfirmId(slot.id)}>
                  REMOVE…
                </button>
              </span>
            )}
            {!confirming && <span className="gms-seat-id">SEAT ID {slot.id}</span>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="be-doc gms-users">
      <div className="gms-users-summary">
        <div className="gms-users-stat">
          <span className="gms-users-stat-num">{slots.length}</span>
          <span className="gms-users-stat-label">SEATS</span>
        </div>
        <div className="gms-users-stat">
          <span className="gms-users-stat-num linked">{linkedCount}</span>
          <span className="gms-users-stat-label">LINKED NOW</span>
        </div>
        <div className="gms-users-stat">
          {/* Rendered off session.passwordSet (broadcast-derived) — NEVER local state. */}
          <span className="gms-users-stat-num">{session.passwordSet ? "ON" : "OFF"}</span>
          <span className="gms-users-stat-label">ROOM LOCK</span>
        </div>
      </div>

      <section className="gms-users-section" aria-labelledby={`${ids}-gm`}>
        <h3 className="gms-users-head" id={`${ids}-gm`}>
          <span className="gms-users-idx">01</span>GAME MASTER
        </h3>
        {you && (
          <div className="gms-users-you">
            <span className="gms-seat-who">
              <span className="gms-seat-name">
                {you.name} <span className="gms-users-you-tag">YOU</span>
              </span>
              <span className="gms-users-you-note">Runs the table. Your own seat can't be demoted or removed.</span>
            </span>
            <SeatStatus linked={you.connected} />
          </div>
        )}
        {coGms.length > 0 && <div className="gms-seat-list">{coGms.map(seatRow)}</div>}
      </section>

      <section className="gms-users-section" aria-labelledby={`${ids}-players`}>
        <h3 className="gms-users-head" id={`${ids}-players`}>
          <span className="gms-users-idx">02</span>PLAYERS
          {players.length > 0 && <span className="gms-users-hint">Tap a seat for its controls.</span>}
        </h3>
        <div className="gms-seat-list">
          {players.length === 0 ? <div className="gms-seat-empty">No player seats yet. Add one below.</div> : players.map(seatRow)}
        </div>
        <label className="gms-users-label" htmlFor={`${ids}-new`}>
          ADD A SEAT
        </label>
        <div className="gms-users-field">
          <input
            id={`${ids}-new`}
            className="be-input"
            placeholder="Player's name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") create();
            }}
          />
          <button type="button" className="be-btn be-btn-primary" aria-label="Add seat" disabled={trimmed === ""} onClick={create}>
            ADD
          </button>
        </div>
      </section>

      <section className="gms-users-section gms-users-lock" aria-labelledby={`${ids}-lock`}>
        <h3 className="gms-users-head" id={`${ids}-lock`}>
          <span className="gms-users-idx">03</span>ROOM LOCK
        </h3>
        <p className="gms-users-lockline">
          {session.passwordSet
            ? "Locked. Anyone joining needs the room password, then picks their seat."
            : "Unlocked. Anyone with the link can pick an open seat."}
        </p>
        <label className="gms-users-label" htmlFor={`${ids}-pw`}>
          {session.passwordSet ? "CHANGE PASSWORD" : "SET A PASSWORD"}
        </label>
        <div className="gms-users-field">
          <input
            id={`${ids}-pw`}
            className="be-input"
            type="password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setPassword();
            }}
          />
          <button type="button" className="be-btn be-btn-primary" aria-label="Set room password" disabled={pwInput === ""} onClick={setPassword}>
            SET
          </button>
        </div>
        {session.passwordSet && (
          <button type="button" className="be-btn be-btn-danger gms-users-unlock" onClick={clearPassword}>
            UNLOCK ROOM
          </button>
        )}
      </section>
      <NotSentNote show={notSent} />
    </div>
  );
}
